import { useEffect, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFDocumentLoadingTask } from 'pdfjs-dist';
// Vite asset URL for the PDF.js worker — the recommended ESM setup, mirroring
// SlideDeckPreview (MED-784). vite-env.d.ts provides the `?url` import type.
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { sessionContentApi } from '../../../services/api/sessionContent';
import type { SessionContentSlot } from '../../../services/api/sessionContent';

// Set the PDF.js worker once for every consumer of the shared slide renderer.
GlobalWorkerOptions.workerSrc = pdfjsWorker;

export interface UseSlideDeckDocParams {
  cohortId: string | null | undefined;
  sessionId: string | null | undefined;
  slot?: SessionContentSlot;
  /**
   * When false the hook does not fetch and clears any loaded doc — e.g. the
   * deck is not `ready`, or there is no deck at all. Gate on deck readiness
   * (NOT the current section's visuals) so the PDF loads once and persists as
   * the leader advances through sections.
   */
  enabled: boolean;
}

export interface UseSlideDeckDocResult {
  doc: PDFDocumentProxy | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads the converted slide-deck PDF as a PDF.js `PDFDocumentProxy`.
 *
 * Extracted from SlideDeckPreview (MED-784) so the live-session surfaces
 * (coach whiteboard, participant slide card — E3.3a) load the deck through one
 * code path. Fetches via the inline `…/content/:slot/pdf` endpoint (served with
 * `Content-Disposition: inline`) and renders client-side, no download.
 */
export function useSlideDeckDoc({
  cohortId,
  sessionId,
  slot = 'slide_deck',
  enabled,
}: UseSlideDeckDocParams): UseSlideDeckDocResult {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !cohortId || !sessionId) {
      setDoc(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let loadedTask: PDFDocumentLoadingTask | null = null;
    setLoading(true);
    setError(null);

    sessionContentApi
      .fetchPdf(cohortId, sessionId, slot)
      .then(async (buffer) => {
        // pdfjs v6 tears the document down through its loading task, not the
        // resolved proxy. PDF scripting stays off unless the annotation layer
        // enables it, which this canvas-only renderer never does, so no
        // getDocument scripting flag is needed (pentest F-2026-20575).
        const task = getDocument({ data: buffer, useSystemFonts: true });
        loadedTask = task;
        const loaded = await task.promise;
        if (cancelled) {
          await task.destroy();
          return;
        }
        setDoc(loaded);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load slide deck.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      void loadedTask?.destroy();
    };
  }, [cohortId, sessionId, slot, enabled]);

  return { doc, loading, error };
}
